import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useBlocker, useNavigate, type LinkProps, type NavLinkProps } from 'react-router';
import { ConfirmDialog } from '@crm/ui';

/**
 * Saisie non enregistrée (grille d'objectifs, prévisions) : le navigateur prévient avant de quitter la page
 * (`beforeunload`), et les liens et changements de mois du module demandent confirmation.
 */
interface UnsavedState {
  setDirty: (key: string, dirty: boolean) => void;
  hasDirty: () => boolean;
  consumeBypass: () => boolean;
  /** Exécute `action` tout de suite s'il n'y a rien à perdre, sinon après confirmation. */
  guard: (action: () => void) => void;
}

const fallback: UnsavedState = { setDirty: () => undefined, hasDirty: () => false, consumeBypass: () => false, guard: (action) => action() };
const UnsavedContext = createContext<UnsavedState | null>(null);

export function UnsavedProvider({ children }: { children: ReactNode }) {
  // Les modules peuvent aussi être montés seuls ; dans l'application ils partagent
  // le registre global afin que le menu et le retour navigateur voient leurs saisies.
  const parent = useContext(UnsavedContext);
  return parent ? <>{children}</> : <RootUnsavedProvider>{children}</RootUnsavedProvider>;
}

function RootUnsavedProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const dirty = useRef(new Set<string>());
  const bypass = useRef(false);
  const [pending, setPending] = useState<(() => void) | null>(null);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirty.current.size) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const setDirty = useCallback((key: string, value: boolean) => {
    if (value) dirty.current.add(key);
    else dirty.current.delete(key);
  }, []);
  const hasDirty = useCallback(() => dirty.current.size > 0, []);
  const consumeBypass = useCallback(() => {
    const allowed = bypass.current;
    bypass.current = false;
    return allowed;
  }, []);
  const guard = useCallback((action: () => void) => {
    if (!dirty.current.size) action();
    else setPending(() => action);
  }, []);
  const state = useMemo(() => ({ setDirty, hasDirty, consumeBypass, guard }), [setDirty, hasDirty, consumeBypass, guard]);

  return (
    <UnsavedContext.Provider value={state}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title={t('performance.unsaved.title')}
        message={t('performance.unsaved.message')}
        confirmLabel={t('performance.unsaved.leave')}
        cancelLabel={t('performance.unsaved.stay')}
        onConfirm={() => {
          const action = pending;
          setPending(null);
          // L'action peut garder un formulaire monté : ses modifications restent protégées.
          // Une navigation synchrone issue de cette confirmation ne doit pas poser deux questions.
          bypass.current = true;
          try { action?.(); } finally { bypass.current = false; }
        }}
      />
    </UnsavedContext.Provider>
  );
}

/** Protège aussi les liens globaux et les boutons précédent/suivant du navigateur. */
export function GlobalNavigationGuard() {
  const { t } = useTranslation();
  const { hasDirty, consumeBypass } = useContext(UnsavedContext) ?? fallback;
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (consumeBypass() || !hasDirty()) return false;
    if (currentLocation.pathname !== nextLocation.pathname) return true;
    // Un changement de paramètres peut remplacer la saisie sans changer le chemin :
    // par exemple un formulaire d'une autre filiale ou les prévisions d'un autre mois.
    return currentLocation.search !== nextLocation.search;
  });
  return <ConfirmDialog
    open={blocker.state === 'blocked'}
    onOpenChange={(open) => { if (!open && blocker.state === 'blocked') blocker.reset(); }}
    title={t('performance.unsaved.title')}
    message={t('performance.unsaved.message')}
    confirmLabel={t('performance.unsaved.leave')}
    cancelLabel={t('performance.unsaved.stay')}
    onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed(); }}
  />;
}

/** Déclare une saisie modifiée (retirée au démontage). */
export function useUnsaved(key: string, dirty: boolean) {
  const { setDirty } = useContext(UnsavedContext) ?? fallback;
  useEffect(() => {
    setDirty(key, dirty);
    return () => setDirty(key, false);
  }, [key, dirty, setDirty]);
  return useCallback(() => setDirty(key, false), [key, setDirty]);
}

export function useGuard() {
  return (useContext(UnsavedContext) ?? fallback).guard;
}

function useGuardedClick(to: string) {
  const guard = useGuard();
  const navigate = useNavigate();
  return (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    guard(() => navigate(to));
  };
}

/** Lien interne qui demande confirmation s'il y a une saisie non enregistrée. */
export function GuardedLink({ to, ...props }: LinkProps & { to: string }) {
  const onClick = useGuardedClick(to);
  return <Link to={to} onClick={onClick} {...props} />;
}

export function GuardedNavLink({ to, ...props }: NavLinkProps & { to: string }) {
  const onClick = useGuardedClick(to);
  return <NavLink to={to} onClick={onClick} {...props} />;
}
